import React, { FormEvent, useCallback, useState, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useAutocompleteSuggestions } from './hooks/useAutocompleteSuggestions';

export const GoogleMapsAutocomplete = ({ location, onPlaceSelect, hasFocus }) => {
    const places = useMapsLibrary('places');
    const [geoLocatorVisible, setGeoLocatorVisible] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [resultValue, setResultValue] = useState(location);
    const { suggestions, resetSession } = useAutocompleteSuggestions(inputValue, {
        includedRegionCodes: ['UK']});
    const containerRef = useRef();
    
    let listIndex = -1;

    const handleInput = useCallback((event) => {
        setInputValue((event.target).value);
        setResultValue((event.target).value);
    }, []);

    const handleSuggestionClick = useCallback(
        async (suggestion) => {
            if (!places) return;
            if (!suggestion.placePrediction) return;

            const place = suggestion.placePrediction.toPlace();

            await place.fetchFields({
                fields: [
                    'viewport',
                    'location'
                ]
            });

            setResultValue(suggestion.placePrediction?.text.text);
            resetSession();
            onPlaceSelect(place, suggestion.placePrediction?.text.text);
        },
        [places, onPlaceSelect]
    );

    function handleArrow(e) {
        const list = containerRef.current.querySelectorAll('ul')[0];
        if (list == null) return;

        const len = list.getElementsByTagName("li").length;

        if (e.which === 40) {
            list.getElementsByTagName("li")[listIndex]?.classList.remove("selected");
            listIndex++;
            if (listIndex > len - 1) {
                listIndex = 0;
            }
            list.getElementsByTagName("li")[listIndex].classList.add("selected");
        } else if (e.which === 38) {
            list.getElementsByTagName("li")[listIndex]?.classList.remove("selected");
            listIndex--;
            if (listIndex < 0) {
                listIndex = len - 1;
            }
            list.getElementsByTagName("li")[listIndex].classList.add("selected");
        } else if (e.key == "Enter") {
            handleSuggestionClick(suggestions[0]); // Just load the first suggestion
        }
    }

    async function getLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    await fetch(
                        `https://api.postcodes.io/outcodes?lon=${position.coords.longitude}&lat=${position.coords.latitude}`,
                        {
                            method: "get",
                        }
                    ).then((response) => {
                        if (response.status < 200 || response.status >= 400) {
                            throw "There was an error retrieving your location. Please try again, or enter manually.";
                        }

                        response
                            .text()
                            .then((text) => {
                                const { status, result } = tryParseJSON(text);

                                if (
                                    status !== 200 ||
                                    !result ||
                                    result.length === 0
                                ) {
                                    throw "There was an error retrieving your location. Please try again, or enter manually.";
                                }

                                setResultValue(result[0].outcode);
                                //setLocationName(result[0].outcode);
                                onPlaceSelect({ geocode: result[0] });
                            })
                            .catch((e) => {
                                alert(e);
                            });
                    });
                }
            );
        } else {
            setGeoLocatorVisible(false);
        }
    }

    function tryParseJSON(jsonString) {
        try {
            const o = JSON.parse(jsonString);
            if (o && typeof o === "object") {
                return o;
            }
        } catch (e) { }

        return JSON.parse("[]");
    }

    function gotFocus() {
        hasFocus('location');
    }

    return (
        <div ref={containerRef} className="autocomplete-container">
            <input id="Location" name="Location" className="location-search rounded" placeholder="e.g. location, postcode, street name" onInput={event => handleInput(event)}
                onChange={e => e.stopPropagation()} onFocus={e => gotFocus()} type="search" autoComplete="off" value={resultValue} onKeyDown={handleArrow}
                onKeyPress={(e) => { e.key === 'Enter' && e.preventDefault(); }} />
            <button type="button" className={geoLocatorVisible ? "geo-icon absolute" : "geo-icon hide"} onClick={getLocation}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M11 8.5V4.56189C7.38128 5.01314 4.51314 7.88128 4.06189 11.5H8V13.5H4.06189C4.51314 17.1187 7.38128 19.9869 11 20.4381V16.5H13V20.4381C16.6187 19.9869 19.4869 17.1187 19.9381 13.5H16V11.5H19.9381C19.4869 7.88128 16.6187 5.01314 13 4.56189V8.5H11ZM2.04938 13.5C2.51844 18.2244 6.27558 21.9816 11 22.4506V24.5H13V22.4506C17.7244 21.9816 21.4816 18.2244 21.9506 13.5H24V11.5H21.9506C21.4816 6.77558 17.7244 3.01844 13 2.54938V0.5H11V2.54938C6.27558 3.01844 2.51844 6.77558 2.04938 11.5H0V13.5H2.04938Z" fill="black" fillOpacity="0.5" />
                </svg>
                {/*<svg xmlns="http://www.w3.org/2000/svg" width="18" height="25" viewBox="0 0 18 25" fill="none">*/}
                {/*    <path fillRule="evenodd" clipRule="evenodd" d="M9 2.60156C5.12742 2.60156 2 5.67838 2 9.40155C2 10.2295 2.40538 11.4639 3.14609 12.9592C3.86827 14.4171 4.84113 15.9915 5.82881 17.4545C6.81407 18.9139 7.80124 20.2437 8.5431 21.2096C8.70873 21.4253 8.86196 21.6226 9 21.7989C9.13804 21.6226 9.29127 21.4253 9.4569 21.2096C10.1988 20.2437 11.1859 18.9139 12.1712 17.4545C13.1589 15.9915 14.1317 14.4171 14.8539 12.9592C15.5946 11.4639 16 10.2295 16 9.40155C16 5.67838 12.8726 2.60156 9 2.60156ZM9 23.4016C8.22853 24.0378 8.22843 24.0377 8.2283 24.0375L8.22789 24.037L8.22653 24.0354L8.22164 24.0294L8.20336 24.0072L8.13413 23.9223C8.07394 23.8482 7.98631 23.7398 7.87516 23.6008C7.6529 23.3228 7.33635 22.9219 6.9569 22.4278C6.19876 21.4407 5.18593 20.0767 4.17119 18.5736C3.15887 17.0741 2.13173 15.4172 1.35391 13.847C0.594622 12.3142 0 10.7236 0 9.40155C0 4.52472 4.07258 0.601562 9 0.601562C13.9274 0.601562 18 4.52472 18 9.40155C18 10.7236 17.4054 12.3142 16.6461 13.847C15.8683 15.4172 14.8411 17.0741 13.8288 18.5736C12.8141 20.0767 11.8012 21.4407 11.0431 22.4278C10.6636 22.9219 10.3471 23.3228 10.1248 23.6008C10.0137 23.7398 9.92606 23.8482 9.86587 23.9223L9.79664 24.0072L9.77836 24.0294L9.77347 24.0354L9.77211 24.037L9.7717 24.0375C9.77157 24.0377 9.77147 24.0378 9 23.4016ZM9 23.4016L9.77147 24.0378C9.58151 24.2681 9.29856 24.4016 9 24.4016C8.70144 24.4016 8.41849 24.2681 8.22853 24.0378L9 23.4016Z" fill="black" fillOpacity="0.5" />*/}
                {/*    <circle cx="9" cy="9.60156" r="4" fill="black" fillOpacity="0.5" />*/}
                {/*</svg>*/}
                <span className="show-for-sr">Use my location</span>
            </button>
            <a href="#" className="hide-for-medium mobile-filter" title="Show filters"></a>
            {suggestions.length > 0 && (
                <ul className="location-list">
                    {suggestions.map((suggestion, index) => {
                        return (
                            <li
                                key={index}
                                className="location-list-item"
                                onClick={() => handleSuggestionClick(suggestion)}
                                onKeyDown={(e) => { e.key === 'Enter' && handleSuggestionClick(suggestion) }} >
                                {suggestion.placePrediction?.text.text}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}